import { useEffect, useState } from 'react'
import dayjs, { Dayjs } from 'dayjs'
import { observer } from 'mobx-react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'

import { TimeUnit } from '~/types'
import { checkMfaBeforeLoad } from '~/utils/beforeLoad'
import { useRootStore } from '~/store/RootStore'

import {
  Button,
  Card,
  ContentWrapper,
  Flex,
  H6,
  Spacer,
} from '~/components/elements'
import {
  ArrowBackIosOutlineIcon,
  ArrowForwardIosOutlineIcon,
} from '~/components/icons'
import { BroadbandUsage } from '~/components/usage/BroadbandUsage'
import { BroadbandDetailedUsage } from '~/components/usage/BroadbandDetailedUsage'
import { VoIPDetailedUsage } from '~/components/usage/VoIPDetailedUsage'

const UsagePage = observer(() => {
  const navigate = useNavigate({ from: '/home' })
  const currentDate = dayjs()
  const { account, usage } = useRootStore()
  const currentService = account.getCurrentService

  const [isLoading, setIsLoading] = useState(true)
  const [usageView, setUsageView] = useState<TimeUnit>(TimeUnit.Month)
  const [usageData, setUsageData] = useState<any>(null)
  const [startDate, setStartDate] = useState(currentDate.subtract(1, 'month'))
  const [endDate, setEndDate] = useState(currentDate.clone())

  useEffect(() => {
    if (usage && currentService) {
      const storeUsageData = usage.getUsage
      const start = dayjs(currentService.nextBillingDate || startDate).subtract(
        1,
        'month'
      )
      const end = dayjs(currentService.nextBillingDate || endDate)
      const key = `${start.format('MM-DD-YYYY')}+${end.format('MM-DD-YYYY')}`

      if (currentService.type.toLowerCase() !== 'voip') {
        if (storeUsageData.broadband.length) {
          if (usageView === TimeUnit.Month) {
            // const month = currentDate.format('MMMM');

            const cachedUsage = storeUsageData.broadband.find(
              (u: any) => u.key === key
            )
            if (cachedUsage) {
              setStartDate(start)
              setEndDate(end)

              setUsageData(cachedUsage.data)
            } else {
              getUsageData(TimeUnit.Month, start, end)
            }
          }
        } else {
          getUsageData(TimeUnit.Month, start, end)
        }
      }
      if (currentService.type.toLowerCase() === 'voip') {
        if (storeUsageData.cdrs.length) {
          const cachedUsage = storeUsageData.cdrs.find(
            (u: any) => u.key === key
          )
          if (cachedUsage) {
            setStartDate(start)
            setEndDate(end)

            setUsageData(cachedUsage.data)
          } else {
            getUsageData(TimeUnit.Month, start, end)
          }
        } else {
          getUsageData(TimeUnit.Month, start, end)
        }
      }
    }
  }, [usage, currentService])

  const getUsageData = (view: TimeUnit, start: Dayjs, end: Dayjs) => {
    setUsageView(view)

    setStartDate(start)
    setEndDate(end)
    setIsLoading(true)

    usage
      .getUsageData(start, end)
      .then((data) => {
        if (data !== null) {
          setUsageData(data)
        }
      })
      .catch((error) => {
        console.error('Error fetching usage data:', error)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  const onClickPrevious = () => {
    const start = startDate.subtract(1, 'month')
    const end = startDate
    getUsageData(TimeUnit.Month, start, end)
  }

  const onClickNext = () => {
    const start = endDate
    const end = endDate.add(1, 'month')
    getUsageData(TimeUnit.Month, start, end)
  }

  return (
    <ContentWrapper>
      <Button
        size="small"
        variant="secondary"
        onClick={() => navigate({ to: '/home' })}
      >
        <ArrowBackIosOutlineIcon /> Back
      </Button>
      <Spacer height="12px" />
      {currentService && currentService.type.toLowerCase() !== 'voip' ? (
        <>
          <H6>Usage</H6>
          <Card>
            <H6 style={{ textAlign: 'center' }}>
              {startDate.format('MMM DD')} - {endDate.format('MMM DD, YYYY')}
            </H6>
            <BroadbandUsage
              loading={isLoading}
              view={usageView}
              usage={usageData}
            />
          </Card>
        </>
      ) : (
        <H6 style={{ textAlign: 'center' }}>
          {startDate.format('MMM DD')} - {endDate.format('MMM DD, YYYY')}
        </H6>
      )}

      <Flex
        alignitems="center"
        justifycontent="space-between"
        style={{ marginBottom: '6px' }}
      >
        <H6 style={{ marginBottom: 0 }}>Detailed Usage</H6>
        <div>
          <Button
            title="Previous Billing Cycle"
            variant="secondary"
            size="small"
            onClick={onClickPrevious}
            disabled={dayjs(
              currentService ? currentService.contractStartDate : currentDate
            ).isSame(startDate, 'month')}
          >
            <ArrowBackIosOutlineIcon />
          </Button>{' '}
          <Button
            title="Next Billing Cycle"
            variant="secondary"
            size="small"
            onClick={onClickNext}
            disabled={
              dayjs(
                currentService ? currentService.nextBillingDate : currentDate
              ).get('month') === endDate.get('month')
            }
          >
            <ArrowForwardIosOutlineIcon />
          </Button>
        </div>
      </Flex>

      {currentService && currentService.type.toLowerCase() !== 'voip' && (
        <BroadbandDetailedUsage usage={usageData} view={usageView} />
      )}
      {currentService && currentService.type.toLowerCase() === 'voip' && (
        <VoIPDetailedUsage loading={isLoading} cdrs={usageData} />
      )}
    </ContentWrapper>
  )
})

export const Route = createFileRoute('/_authenticated/home/usage')({
  beforeLoad: checkMfaBeforeLoad,
  component: UsagePage,
})
